import { Component, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { WorkoutTrackerService } from './workout-tracker.service';
import { User, Workout, Goal, Exercise, WorkoutExercise, TrackingType, Routine, RoutineExercise, Set as WorkoutSet } from './workout-tracker.model';
import { Observable, of } from 'rxjs';
import { switchMap, map, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { WorkoutLogComponent } from './workout-log.component';

@Component({
    selector: 'app-workout-tracker',
    templateUrl: './workout-tracker.component.html',
    styleUrls: ['./workout-tracker.component.scss'],
    standalone: false
})
export class WorkoutTrackerComponent implements OnInit {
  user$: Observable<User | null>;
  currentUser: User | null = null;
  isAuthenticated = false;
  workouts$: Observable<Workout[]> = of([]);
  goals: Goal[] = [];
  workoutsThisWeek = 0;
  today = new Date();
  loading = true;
  authLoading = true;
  activeTab: 'dashboard' | 'workouts' | 'exercises' | 'routines' = 'dashboard';
  showWorkoutForm = false;
  exercises: Exercise[] = [];
  workoutLog: Workout[] = [];
  newWorkout: Partial<Workout> = {
    name: '',
    date: '',
    notes: '',
    exercises: []
  };
  workoutFormError = '';

  showAddExerciseModal = false;
  newExercise: Partial<Exercise> = { name: '', description: '', muscleGroups: [], trackingType: 'reps_weight' };
  addExerciseFormError = '';
  addExerciseTargetIdx: number | null = null;
  isEditingExercise = false;
  editingExerciseId: string | null = null;
  showSignUp = false;

  trackingTypeOptions: { value: TrackingType; label: string }[] = [
    { value: 'reps_weight', label: 'Reps + Weight' },
    { value: 'reps_only', label: 'Reps Only' },
    { value: 'duration', label: 'Duration' },
    { value: 'distance_duration', label: 'Distance + Duration' },
  ];

  muscleGroupOptions: string[] = [
    'Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Forearms',
    'Abs', 'Obliques', 'Quads', 'Hamstrings', 'Glutes', 'Calves',
    'Traps', 'Neck', 'Lower Back', 'Adductors', 'Abductors', 'Hip Flexors'
  ];

  // Map muscle group names to icon/image URLs or SVGs
  // muscleGroupIcons: { [key: string]: string } = {
  //   'Chest': 'assets/icons/muscle-chest.svg',
  //   'Back': 'assets/icons/muscle-back.svg',
  //   'Shoulders': 'assets/icons/muscle-shoulders.svg',
  //   'Biceps': 'assets/icons/muscle-biceps.svg',
  //   'Triceps': 'assets/icons/muscle-triceps.svg',
  //   'Forearms': 'assets/icons/muscle-forearms.svg',
  //   'Abs': 'assets/icons/muscle-abs.svg',
  //   'Obliques': 'assets/icons/muscle-obliques.svg',
  //   'Quads': 'assets/icons/muscle-quads.svg',
  //   'Hamstrings': 'assets/icons/muscle-hamstrings.svg',
  //   'Glutes': 'assets/icons/muscle-glutes.svg',
  //   'Calves': 'assets/icons/muscle-calves.svg',
  //   'Traps': 'assets/icons/muscle-traps.svg',
  //   'Neck': 'assets/icons/muscle-neck.svg',
  //   'Lower Back': 'assets/icons/muscle-lowerback.svg',
  //   'Adductors': 'assets/icons/muscle-adductors.svg',
  //   'Abductors': 'assets/icons/muscle-abductors.svg',
  //   'Hip Flexors': 'assets/icons/muscle-hipflexors.svg',
  // };

  openExerciseIdx: number | null = null;
  editExerciseIdx: number | null = null;

  // Per-exercise search state
  exerciseSearchTerms: string[] = [];
  exerciseFilterOpen: boolean[] = [];
  exerciseFilterMuscle: (string | null)[] = [];
  exerciseDropdownOpen: boolean[] = [];

  // New state for workout details modal
  showWorkoutModal = false;
  selectedWorkout: Workout | null = null;
  expandedExercises: Set<number> = new Set();
  editingWorkoutId: string | null = null;
  showShadows = true;

  // Routines tab
  routines: Routine[] = [];
  showRoutineForm = false;
  newRoutine: Partial<Routine> = { name: '', exercises: [] };
  editingRoutineId: string | null = null;
  routineFormError = '';
  routineExerciseSearchTerms: string[] = [];
  routineExerciseFilterOpen: boolean[] = [];
  routineExerciseFilterMuscle: (string | null)[] = [];
  routineExerciseDropdownOpen: boolean[] = [];

  constructor(private afAuth: AngularFireAuth, private tracker: WorkoutTrackerService) {
    this.user$ = this.afAuth.authState.pipe(
      switchMap(authUser => {
        if (!authUser) { 
          this.isAuthenticated = false;
          this.currentUser = null;
          this.authLoading = false;
          return of(null); 
        }
        this.isAuthenticated = true;
        
        // Check if user profile exists, create if not
        return this.tracker.getUser(authUser.uid).pipe(
          switchMap(dbUser => {
            if (!dbUser) {
              // Create new user profile
              const newUser: User = {
                id: authUser.uid,
                displayName: authUser.displayName || '',
                email: authUser.email || '',
                goals: []
              };
              // Only add avatarUrl if it exists
              if (authUser.photoURL) {
                newUser.avatarUrl = authUser.photoURL;
              }
              return this.tracker.setUser(newUser).then(() => newUser);
            }
            return of(dbUser);
          }),
          map(user => {
            this.currentUser = user;
            this.authLoading = false;
            return user;
          })
        );
      })
    );
  }

  ngOnInit() {
    console.log('init workout tracker component');
    this.user$.subscribe(user => {
      if (user && this.isAuthenticated) {
        this.loadUserData(user);
      } else {
        this.resetData();
      }
    });
    this.resetWorkoutForm();
  }

  private loadUserData(user: User) {
    this.goals = user.goals || [];
    
    // Load user's workouts
    this.tracker.getWorkouts(user.id).subscribe(workouts => {
      this.workoutsThisWeek = this.countWorkoutsThisWeek(workouts);
      this.workoutLog = workouts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      this.loading = false;
    });
    
    // Load exercises (global for now, but could be user-specific)
    this.tracker.getExercises().subscribe(exs => {
      this.exercises = exs;
    });

    // Load user's routines
    this.tracker.getRoutines(user.id).subscribe(rs => {
      this.routines = rs || [];
    });
  }

  private resetData() {
    this.goals = [];
    this.workoutsThisWeek = 0;
    this.workoutLog = [];
    this.exercises = [];
    this.routines = [];
    this.loading = false;
  }

  countWorkoutsThisWeek(workouts: Workout[]): number {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
    return workouts.filter(w => {
      const d = new Date(w.date);
      return d >= startOfWeek && d <= now;
    }).length;
  }

  logout() {
    return this.afAuth.signOut().then(() => {
      this.activeTab = 'dashboard';
      this.showWorkoutForm = false;
      console.log('User signed out');
    });
  }

  async signInWithGoogle() {
    try {
      const { GoogleAuthProvider } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      await this.afAuth.signInWithPopup(provider);
      console.log('User signed in with Google');
    } catch (error) {
      console.error('Error signing in with Google:', error);
    }
  }

  async signInWithEmail(email: string, password: string) {
    try {
      await this.afAuth.signInWithEmailAndPassword(email, password);
      console.log('User signed in with email');
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  }

  async signUpWithEmail(email: string, password: string, displayName: string) {
    try {
      const credential = await this.afAuth.createUserWithEmailAndPassword(email, password);
      if (credential.user) {
        await credential.user.updateProfile({ displayName });
        console.log('User signed up and profile updated');
      }
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  }

  handleEmailLogin(form: any) {
    if (form.valid) {
      const { email, password } = form.value;
      this.signInWithEmail(email, password).catch(error => {
        console.error('Login failed:', error);
        alert('Login failed. Please check your credentials.');
      });
    }
  }

  handleEmailSignup(form: any) {
    if (form.valid) {
      const { displayName, signupEmail, signupPassword } = form.value;
      this.signUpWithEmail(signupEmail, signupPassword, displayName).catch(error => {
        console.error('Signup failed:', error);
        alert('Signup failed. Please try again.');
      });
    }
  }

  addWorkout() {
    this.workoutFormError = '';

    if (!this.isAuthenticated || !this.currentUser) {
      this.workoutFormError = 'You must be logged in to save workouts.';
      return;
    }

    if (!this.newWorkout.name || !this.newWorkout.date || !this.newWorkout.exercises?.length) {
      this.workoutFormError = 'Please fill out all required fields and add at least one exercise.';
      return;
    }

    const workout: Workout = {
      id: this.editingWorkoutId || uuidv4(),
      userId: this.currentUser.id,
      name: this.newWorkout.name || '',
      date: this.newWorkout.date || '',
      notes: this.newWorkout.notes || '',
      exercises: (this.newWorkout.exercises as WorkoutExercise[]).map(ex => ({
        exerciseId: ex.exerciseId,
        sets: ex.sets.map(s => ({ ...s })),
        notes: ex.notes || ''
      })),
      ...(this.newWorkout.routineId ? { routineId: this.newWorkout.routineId } : {})
    };

    // Firebase rejects undefined values — strip them before saving
    const cleanWorkout: Workout = JSON.parse(JSON.stringify(workout));

    const savePromise = this.editingWorkoutId
      ? this.tracker.updateWorkout(cleanWorkout)
      : this.tracker.addWorkout(cleanWorkout);

    savePromise.then(() => {
      this.showWorkoutForm = false;
      this.resetWorkoutForm();
    }).catch(error => {
      this.workoutFormError = 'Failed to save workout. Please try again.';
      console.error('Error saving workout:', error);
    });
  }

  cancelWorkoutForm() {
    this.showWorkoutForm = false;
    this.resetWorkoutForm();
  }

  resetWorkoutForm() {
    this.editingWorkoutId = null;
    const todayStr = new Date().toISOString().slice(0, 10);
    this.newWorkout = { name: '', date: todayStr, notes: '', exercises: [] };
    this.workoutFormError = '';
    this.openExerciseIdx = null;
    this.editExerciseIdx = null;
    this.exerciseSearchTerms = [];
    this.exerciseFilterOpen = [];
    this.exerciseFilterMuscle = [];
    this.exerciseDropdownOpen = [];
    this.addExerciseToWorkout(); // Add the first exercise automatically
  }

  addExerciseToWorkout() {
    (this.newWorkout.exercises as WorkoutExercise[]).push({
      exerciseId: '',
      sets: [{}],
      notes: ''
    });
    this.exerciseSearchTerms.push('');
    this.exerciseFilterOpen.push(false);
    this.exerciseFilterMuscle.push(null);
    this.exerciseDropdownOpen.push(false);
    if ((this.newWorkout.exercises as WorkoutExercise[]).length === 1) {
      this.openExerciseIdx = 0;
    }
  }

  setOpenExercise(idx: number) {
    this.openExerciseIdx = idx;
  }

  toggleOpenExercise(idx: number) {
    this.openExerciseIdx = this.openExerciseIdx === idx ? null : idx;
  }

  toggleExerciseActions(idx: number) {
    this.editExerciseIdx = this.editExerciseIdx === idx ? null : idx;
  }

  trackByExerciseIndex(index: number): number {
    return index;
  }

  removeExerciseFromWorkout(idx: number) {
    (this.newWorkout.exercises as WorkoutExercise[]).splice(idx, 1);
    this.exerciseSearchTerms.splice(idx, 1);
    this.exerciseFilterOpen.splice(idx, 1);
    this.exerciseFilterMuscle.splice(idx, 1);
    this.exerciseDropdownOpen.splice(idx, 1);
    this.editExerciseIdx = null;
    if (this.openExerciseIdx === idx) {
      this.openExerciseIdx = null;
    } else if (this.openExerciseIdx && idx < this.openExerciseIdx) {
      this.openExerciseIdx--;
    }
  }

  addSetToExercise(exIdx: number) {
    ((this.newWorkout.exercises as WorkoutExercise[])[exIdx].sets).push({});
  }

  removeSetFromExercise(exIdx: number, setIdx: number) {
    ((this.newWorkout.exercises as WorkoutExercise[])[exIdx].sets).splice(setIdx, 1);
  }

  repeatLastSet(exIdx: number) {
    const exercise = (this.newWorkout.exercises as WorkoutExercise[])[exIdx];
    if (exercise.sets.length > 0) {
      const lastSet = exercise.sets[exercise.sets.length - 1];
      exercise.sets.push({ reps: lastSet.reps, weight: lastSet.weight, restTime: lastSet.restTime });
    }
  }

  openAddExerciseModal(exIdx: number | null, exerciseToEdit?: Exercise) {
    this.showAddExerciseModal = true;
    this.addExerciseTargetIdx = exIdx;
    this.isEditingExercise = !!exerciseToEdit;
    this.editingExerciseId = exerciseToEdit?.id || null;
    
    if (exerciseToEdit) {
      // Populate form with existing exercise data
      this.newExercise = {
        name: exerciseToEdit.name,
        description: exerciseToEdit.description || '',
        muscleGroups: exerciseToEdit.muscleGroups ? [...exerciseToEdit.muscleGroups] : [],
        trackingType: exerciseToEdit.trackingType || 'reps_weight'
      };
    } else {
      // Reset form for new exercise
      this.newExercise = { name: '', description: '', muscleGroups: [], trackingType: 'reps_weight' };
    }
    this.addExerciseFormError = '';
  }

  closeAddExerciseModal() {
    this.showAddExerciseModal = false;
    this.addExerciseTargetIdx = null;
    this.newExercise = { name: '', description: '', muscleGroups: [], trackingType: 'reps_weight' };
    this.addExerciseFormError = '';
    this.isEditingExercise = false;
    this.editingExerciseId = null;
  }

  saveNewExercise() {
    this.addExerciseFormError = '';
    
    if (!this.isAuthenticated) {
      this.addExerciseFormError = 'You must be logged in to save exercises.';
      return;
    }
    
    if (!this.newExercise.name) {
      this.addExerciseFormError = 'Exercise name is required.';
      return;
    }

    if (this.isEditingExercise && this.editingExerciseId) {
      // Update existing exercise
      const updatedExercise: Exercise = {
        id: this.editingExerciseId,
        name: this.newExercise.name,
        description: this.newExercise.description || '',
        muscleGroups: this.newExercise.muscleGroups || [],
        trackingType: this.newExercise.trackingType || 'reps_weight'
      };
      
      this.tracker.updateExercise(updatedExercise).then(() => {
        this.tracker.getExercises().subscribe(exs => {
          this.exercises = exs;
          console.log('Exercise updated:', updatedExercise);
          this.closeAddExerciseModal();
        });
      }).catch(error => {
        this.addExerciseFormError = 'Failed to update exercise. Please try again.';
        console.error('Error updating exercise:', error);
      });
    } else {
      // Add new exercise
      const exercise: Exercise = {
        id: uuidv4(),
        name: this.newExercise.name,
        description: this.newExercise.description || '',
        muscleGroups: this.newExercise.muscleGroups || [],
        trackingType: this.newExercise.trackingType || 'reps_weight'
      };
      
      this.tracker.addExercise(exercise).then(() => {
        this.tracker.getExercises().subscribe(exs => {
          this.exercises = exs;
          if (this.addExerciseTargetIdx !== null) {
            (this.newWorkout.exercises as WorkoutExercise[])[this.addExerciseTargetIdx].exerciseId = exercise.id;
          }
          this.closeAddExerciseModal();
        });
      }).catch(error => {
        this.addExerciseFormError = 'Failed to add exercise. Please try again.';
        console.error('Error adding exercise:', error);
      });
    }
  }

  toggleMuscleGroup(mg: string) {
    if (!this.newExercise.muscleGroups) {this.newExercise.muscleGroups = []; }
    const idx = this.newExercise.muscleGroups.indexOf(mg);
    if (idx > -1) {
      this.newExercise.muscleGroups = this.newExercise.muscleGroups.filter(m => m !== mg);
    } else {
      this.newExercise.muscleGroups = [...this.newExercise.muscleGroups, mg];
    }
  }

  getExerciseNameById(id: string): string {
    const ex = this.exercises.find(e => e.id === id);
    return ex ? ex.name : '';
  }

  // --- Exercise search/filter ---
  getFilteredExercises(exIdx: number): Exercise[] {
    const term = (this.exerciseSearchTerms[exIdx] || '').toLowerCase().trim();
    const muscle = this.exerciseFilterMuscle[exIdx];
    return this.exercises.filter(e => {
      const matchesName = !term || e.name.toLowerCase().includes(term);
      const matchesMuscle = !muscle || (e.muscleGroups || []).includes(muscle);
      return matchesName && matchesMuscle;
    });
  }

  hasExactMatch(exIdx: number): boolean {
    const term = (this.exerciseSearchTerms[exIdx] || '').toLowerCase().trim();
    if (!term) return true;
    return this.exercises.some(e => e.name.toLowerCase() === term);
  }

  selectExercise(exIdx: number, exercise: Exercise) {
    (this.newWorkout.exercises as WorkoutExercise[])[exIdx].exerciseId = exercise.id;
    this.exerciseDropdownOpen[exIdx] = false;
    this.exerciseSearchTerms[exIdx] = '';
  }

  toggleExerciseFilter(exIdx: number) {
    this.exerciseFilterOpen[exIdx] = !this.exerciseFilterOpen[exIdx];
  }

  setExerciseMuscleFilter(exIdx: number, muscle: string | null) {
    this.exerciseFilterMuscle[exIdx] = muscle;
  }

  onExerciseSearchFocus(exIdx: number) {
    this.exerciseDropdownOpen[exIdx] = true;
  }

  onExerciseSearchBlur(exIdx: number) {
    setTimeout(() => { this.exerciseDropdownOpen[exIdx] = false; }, 150);
  }

  createExerciseFromSearch(exIdx: number) {
    const term = this.exerciseSearchTerms[exIdx] || '';
    this.showAddExerciseModal = true;
    this.addExerciseTargetIdx = exIdx;
    this.isEditingExercise = false;
    this.editingExerciseId = null;
    this.newExercise = { name: term, description: '', muscleGroups: [], trackingType: 'reps_weight' };
    this.addExerciseFormError = '';
  }

  // --- Routine builder ---
  openRoutineForm(routine?: Routine) {
    this.showRoutineForm = true;
    this.routineFormError = '';
    if (routine) {
      this.editingRoutineId = routine.id;
      const ordered = [...routine.exercises].sort((a, b) => a.order - b.order);
      this.newRoutine = { name: routine.name, exercises: ordered.map(e => ({ exerciseId: e.exerciseId, order: e.order })) };
      const count = ordered.length;
      this.routineExerciseSearchTerms = Array(count).fill('');
      this.routineExerciseFilterOpen = Array(count).fill(false);
      this.routineExerciseFilterMuscle = Array(count).fill(null);
      this.routineExerciseDropdownOpen = Array(count).fill(false);
    } else {
      this.editingRoutineId = null;
      this.newRoutine = { name: '', exercises: [] };
      this.routineExerciseSearchTerms = [];
      this.routineExerciseFilterOpen = [];
      this.routineExerciseFilterMuscle = [];
      this.routineExerciseDropdownOpen = [];
      this.addExerciseToRoutine();
    }
  }

  cancelRoutineForm() {
    this.showRoutineForm = false;
    this.editingRoutineId = null;
    this.newRoutine = { name: '', exercises: [] };
    this.routineFormError = '';
  }

  addExerciseToRoutine() {
    const exs = this.newRoutine.exercises as RoutineExercise[];
    exs.push({ exerciseId: '', order: exs.length });
    this.routineExerciseSearchTerms.push('');
    this.routineExerciseFilterOpen.push(false);
    this.routineExerciseFilterMuscle.push(null);
    this.routineExerciseDropdownOpen.push(false);
  }

  removeRoutineExercise(idx: number) {
    (this.newRoutine.exercises as RoutineExercise[]).splice(idx, 1);
    this.routineExerciseSearchTerms.splice(idx, 1);
    this.routineExerciseFilterOpen.splice(idx, 1);
    this.routineExerciseFilterMuscle.splice(idx, 1);
    this.routineExerciseDropdownOpen.splice(idx, 1);
  }

  moveRoutineExercise(idx: number, dir: -1 | 1) {
    const exs = this.newRoutine.exercises as RoutineExercise[];
    const target = idx + dir;
    if (target < 0 || target >= exs.length) return;
    [exs[idx], exs[target]] = [exs[target], exs[idx]];
    [this.routineExerciseSearchTerms[idx], this.routineExerciseSearchTerms[target]] =
      [this.routineExerciseSearchTerms[target], this.routineExerciseSearchTerms[idx]];
    [this.routineExerciseFilterMuscle[idx], this.routineExerciseFilterMuscle[target]] =
      [this.routineExerciseFilterMuscle[target], this.routineExerciseFilterMuscle[idx]];
    [this.routineExerciseFilterOpen[idx], this.routineExerciseFilterOpen[target]] =
      [this.routineExerciseFilterOpen[target], this.routineExerciseFilterOpen[idx]];
    [this.routineExerciseDropdownOpen[idx], this.routineExerciseDropdownOpen[target]] =
      [this.routineExerciseDropdownOpen[target], this.routineExerciseDropdownOpen[idx]];
  }

  // --- Routine exercise picker (parallels the workout-form picker) ---
  getRoutineFilteredExercises(idx: number): Exercise[] {
    const term = (this.routineExerciseSearchTerms[idx] || '').toLowerCase().trim();
    const muscle = this.routineExerciseFilterMuscle[idx];
    return this.exercises.filter(e => {
      const matchesName = !term || e.name.toLowerCase().includes(term);
      const matchesMuscle = !muscle || (e.muscleGroups || []).includes(muscle);
      return matchesName && matchesMuscle;
    });
  }

  selectRoutineExercise(idx: number, exercise: Exercise) {
    (this.newRoutine.exercises as RoutineExercise[])[idx].exerciseId = exercise.id;
    this.routineExerciseDropdownOpen[idx] = false;
    this.routineExerciseSearchTerms[idx] = '';
  }

  onRoutineSearchFocus(idx: number) { this.routineExerciseDropdownOpen[idx] = true; }
  onRoutineSearchBlur(idx: number) { setTimeout(() => { this.routineExerciseDropdownOpen[idx] = false; }, 150); }
  toggleRoutineFilter(idx: number) { this.routineExerciseFilterOpen[idx] = !this.routineExerciseFilterOpen[idx]; }
  setRoutineMuscleFilter(idx: number, muscle: string | null) { this.routineExerciseFilterMuscle[idx] = muscle; }

  saveRoutine() {
    this.routineFormError = '';
    if (!this.isAuthenticated || !this.currentUser) {
      this.routineFormError = 'You must be logged in to save routines.';
      return Promise.resolve();
    }
    if (!this.newRoutine.name?.trim()) {
      this.routineFormError = 'Routine name is required.';
      return Promise.resolve();
    }
    const selected = (this.newRoutine.exercises as RoutineExercise[]).filter(e => e.exerciseId);
    if (!selected.length) {
      this.routineFormError = 'Add at least one exercise.';
      return Promise.resolve();
    }
    const userId = this.currentUser.id;
    const routine: Routine = {
      id: this.editingRoutineId || uuidv4(),
      userId,
      name: this.newRoutine.name.trim(),
      exercises: selected.map((e, i) => ({ exerciseId: e.exerciseId, order: i })),
    };
    // Firebase rejects undefined values — strip them before saving
    const clean: Routine = JSON.parse(JSON.stringify(routine));
    const save = this.editingRoutineId ? this.tracker.updateRoutine(clean) : this.tracker.addRoutine(clean);
    return save.then(() => {
      this.tracker.getRoutines(userId).subscribe(rs => { this.routines = rs || []; });
      this.cancelRoutineForm();
    }).catch(err => {
      this.routineFormError = 'Failed to save routine. Please try again.';
      console.error('Error saving routine:', err);
    });
  }

  deleteRoutine(routine: Routine) {
    if (!this.isAuthenticated || !this.currentUser) {
      alert('You must be logged in to delete routines.');
      return;
    }
    if (confirm(`Delete routine "${routine.name}"?`)) {
      const userId = this.currentUser.id;
      this.tracker.deleteRoutine(routine.id).then(() => {
        this.tracker.getRoutines(userId).subscribe(rs => { this.routines = rs || []; });
      }).catch(err => {
        console.error('Error deleting routine:', err);
        alert('Failed to delete routine. Please try again.');
      });
    }
  }

  startWorkoutFromRoutine(routine: Routine) {
    const ordered = [...routine.exercises].sort((a, b) => a.order - b.order);
    const todayStr = new Date().toISOString().slice(0, 10);
    this.editingWorkoutId = null;
    this.newWorkout = {
      name: routine.name,
      date: todayStr,
      notes: '',
      routineId: routine.id,
      exercises: ordered.map(e => ({ exerciseId: e.exerciseId, sets: [{}], notes: '' })),
    };
    const count = ordered.length;
    this.exerciseSearchTerms = Array(count).fill('');
    this.exerciseFilterOpen = Array(count).fill(false);
    this.exerciseFilterMuscle = Array(count).fill(null);
    this.exerciseDropdownOpen = Array(count).fill(false);
    this.openExerciseIdx = 0;
    this.workoutFormError = '';
    this.expandedExercises.clear();
    this.showRoutineForm = false;
    this.activeTab = 'workouts';
    this.showWorkoutForm = true;
  }

  getExerciseType(exerciseId: string): TrackingType {
    const ex = this.exercises.find(e => e.id === exerciseId);
    return ex?.trackingType || 'reps_weight';
  }

  getLastSession(exerciseId: string): { date: string; sets: WorkoutSet[] } | null {
    if (!exerciseId) return null;
    for (const w of this.workoutLog) {
      if (this.editingWorkoutId && w.id === this.editingWorkoutId) continue;
      const we = w.exercises?.find(e => e.exerciseId === exerciseId);
      if (we && we.sets && we.sets.length) {
        return { date: w.date, sets: we.sets };
      }
    }
    return null;
  }

  getShadowSet(exerciseId: string, setIdx: number): WorkoutSet | null {
    const last = this.getLastSession(exerciseId);
    return last && setIdx < last.sets.length ? last.sets[setIdx] : null;
  }

  formatShadowSet(set: WorkoutSet, type: TrackingType): string {
    switch (type) {
      case 'reps_only': return `${set.reps ?? 0} reps`;
      case 'duration': return `${set.duration ?? 0} min`;
      case 'distance_duration': return `${set.distance ?? 0} mi · ${set.duration ?? 0} min`;
      case 'reps_weight':
      default: return `${set.reps ?? 0} × ${set.weight ?? 0}`;
    }
  }

  applyShadow(exIdx: number, setIdx: number) {
    const exId = (this.newWorkout.exercises as WorkoutExercise[])[exIdx].exerciseId;
    const shadow = this.getShadowSet(exId, setIdx);
    if (!shadow) return;
    const set = (this.newWorkout.exercises as WorkoutExercise[])[exIdx].sets[setIdx];
    if (shadow.reps != null) set.reps = shadow.reps;
    if (shadow.weight != null) set.weight = shadow.weight;
    if (shadow.duration != null) set.duration = shadow.duration;
    if (shadow.distance != null) set.distance = shadow.distance;
  }

  getAverageDuration(sets: any[]): number {
    if (!sets || sets.length === 0) return 0;
    const total = sets.reduce((sum, set) => sum + (set.duration || 0), 0);
    return Math.round(total / sets.length * 10) / 10;
  }

  getAverageDistance(sets: any[]): number {
    if (!sets || sets.length === 0) return 0;
    const total = sets.reduce((sum, set) => sum + (set.distance || 0), 0);
    return Math.round(total / sets.length * 10) / 10;
  }

  openWorkoutModal(w: Workout) {
    this.selectedWorkout = w;
    this.showWorkoutModal = true;
  }
  closeWorkoutModal() {
    this.showWorkoutModal = false;
    this.selectedWorkout = null;
    this.expandedExercises.clear();
  }

  openEditWorkout(workout: Workout) {
    this.closeWorkoutModal();
    this.editingWorkoutId = workout.id;
    this.newWorkout = {
      name: workout.name,
      date: workout.date,
      notes: workout.notes || '',
      exercises: workout.exercises.map(ex => ({
        exerciseId: ex.exerciseId,
        sets: ex.sets.map(s => ({ ...s })),
        notes: ex.notes || ''
      })),
      ...(workout.routineId ? { routineId: workout.routineId } : {})
    };
    const count = workout.exercises.length;
    this.exerciseSearchTerms = Array(count).fill('');
    this.exerciseFilterOpen = Array(count).fill(false);
    this.exerciseFilterMuscle = Array(count).fill(null);
    this.exerciseDropdownOpen = Array(count).fill(false);
    this.openExerciseIdx = null;
    this.workoutFormError = '';
    this.activeTab = 'workouts';
    this.showWorkoutForm = true;
  }

  toggleExerciseExpanded(index: number) {
    if (this.expandedExercises.has(index)) {
      this.expandedExercises.delete(index);
    } else {
      this.expandedExercises.add(index);
    }
  }

  isExerciseExpanded(index: number): boolean {
    return this.expandedExercises.has(index);
  }

  getAverageReps(sets: any[]): number {
    if (!sets || sets.length === 0) return 0;
    const total = sets.reduce((sum, set) => sum + (set.reps || 0), 0);
    return Math.round(total / sets.length * 10) / 10;
  }

  getAverageWeight(sets: any[]): number {
    if (!sets || sets.length === 0) return 0;
    const total = sets.reduce((sum, set) => sum + (set.weight || 0), 0);
    return Math.round(total / sets.length * 10) / 10;
  }

  // Add handler for workout selection
  onWorkoutSelected(w: Workout) {
    this.openWorkoutModal(w);
  }

  onEditExercise(ex: Exercise) {
    this.openAddExerciseModal(null, ex);
  }
  onDeleteExercise(ex: Exercise) {
    if (!this.isAuthenticated) {
      alert('You must be logged in to delete exercises.');
      return;
    }
    
    if (confirm(`Are you sure you want to delete the exercise: ${ex.name}?`)) {
      this.tracker.deleteExercise(ex.id).then(() => {
        this.tracker.getExercises().subscribe(exs => {
          this.exercises = exs;
        });
      }).catch(error => {
        console.error('Error deleting exercise:', error);
        alert('Failed to delete exercise. Please try again.');
      });
    }
  }
}
